import "./backupModal.scss";

import { List } from "immutable";
import { flatten, map, reverse, sortBy } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import PropagateLoader from "react-spinners/PropagateLoader";

import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import FormTextField from "../../components/form/FormTextField";
import ModalBody from "../../components/modal/ModalBody";
import ModalDetailView from "../../components/modal/ModalDetailView";
import ModalField from "../../components/modal/ModalField";
import ModalGroup from "../../components/modal/ModalGroup";
import ModalHeading from "../../components/modal/ModalHeading";
import ModalRow from "../../components/modal/ModalRow";
import ModalView from "../../components/modal/ModalView";
import ToolbarPopoverButton from "../../components/toolbar/ToolbarPopoverButton";
import ToolbarPopoverList from "../../components/toolbar/ToolbarPopoverList";
import ToolbarTextButton from "../../components/toolbar/ToolbarTextButton";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions from "../../constants/styles/dimensions";
import dialogs from "../../dialogs";
import { preferencesOperations, preferencesSelectors } from "../../modules/preferences";
import { uiStateOperations } from "../../modules/uiState";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import { immutableRecords } from "../../types";

const LATEST_BACKUP_NAME = "latest.aeonBk";

const deleteOptions = List([
  immutableRecords.PopoverMenuGroupRecord({
    children: List([
      immutableRecords.PopoverMenuItemRecord({
        id: nativeCommsBroadcastMessageTypes.DELETE_ALL_BACKUPS_FOR_FILE,
        label: "Delete all"
      }),
      immutableRecords.PopoverMenuItemRecord({
        id: nativeCommsBroadcastMessageTypes.DELETE_ALL_BUT_LATEST_BACKUP_FOR_FILE,
        label: "Delete all but latest"
      })
    ])
  })
]);

const optionToRecoverFile = f => {
  return immutableRecords.PopoverMenuItemRecord({
    id: f.fileName,
    label: convertDateString(f.fileName)
  });
};

const recoverOptionsForGroup = group => {
  const children = List(group.backupFiles.map(f => optionToRecoverFile(f))).insert(
    0,
    immutableRecords.PopoverMenuItemRecord({
      id: LATEST_BACKUP_NAME,
      label: "Latest"
    })
  );

  return List([
    immutableRecords.PopoverMenuGroupRecord({
      children
    })
  ]);
};

const splitFilePath = filepath => {
  const lastSlash = filepath.lastIndexOf("/");
  const fileName = filepath.substr(lastSlash + 1);
  const fileDir = filepath.substr(0, lastSlash);
  return {
    fileName,
    fileDir
  };
};

const to2DigitString = n => (n <= 9 ? `0${n}` : String(n));

const convertDateString = dateStr => {
  const arr = flatten(map(dateStr.split("."), s => s.split("-")));
  if (arr.length < 6) {
    return "N/A";
  }
  const date = new Date(arr[0], arr[1] - 1, arr[2], arr[3], arr[4], arr[5]);
  const hh = to2DigitString(date.getHours());
  const mm = to2DigitString(date.getMinutes());
  const ss = to2DigitString(date.getSeconds());
  return `${date.toDateString()} ${hh}:${mm}:${ss}`;
};

const convertFileSizeToString = size => {
  const kb = 1024;
  const mb = 1024 * 1024;
  const gb = 1024 * 1024 * 1024;
  if (size < kb) {
    return `${size} bytes`;
  }
  if (size < mb) {
    return `${(size / kb).toFixed(1)} KB`;
  }
  if (size < gb) {
    return `${(size / mb).toFixed(1)} MB`;
  }
  return `${(size / gb).toFixed(1)} GB`;
};

const displayDirectoryString = fileDir => {
  return fileDir.replace(/^\/Users\/\w+\//, "~/");
};

const renderFileGroup = (group, onRecoverClick, onDeleteClick) => {
  const filepath = group.originalFilePath;
  const { fileName, fileDir } = splitFilePath(filepath);
  const latestBackupDate = convertDateString(group.latestBackupDate);
  const totalSize = convertFileSizeToString(group.totalBackupSize);
  const numFiles = group.numBackupFiles;
  const copiesStr = `${numFiles} ${numFiles === 1 ? "copy" : "copies"}`;

  const recoverButton = (
    <ToolbarPopoverButton placement="bottom-start" popoverClassName="backupActionPopover">
      <ToolbarTextButton id={`recover-${group.guid}`} text="Recover" />
      <ToolbarPopoverList
        groups={recoverOptionsForGroup(group)}
        showSelectedIcon={false}
        onClick={backupFile => onRecoverClick(group.originalFilePath, backupFile)}
      />
    </ToolbarPopoverButton>
  );

  const deleteButton = (
    <ToolbarPopoverButton placement="bottom-start" popoverClassName="backupActionPopover">
      <ToolbarTextButton id={`delete-${group.guid}`} text="Delete" />
      <ToolbarPopoverList
        groups={deleteOptions}
        showSelectedIcon={false}
        onClick={msg => onDeleteClick(msg, group.originalFilePath)}
      />
    </ToolbarPopoverButton>
  );

  return (
    <ModalGroup key={group.guid}>
      <ModalRow>
        <ModalField>
          <div className="backupFileInfoRow">
            <div className="fileName">{fileName}</div>
            <div className="numFiles">{copiesStr}</div>
            <div className="totalSize">{totalSize}</div>
          </div>
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField>
          <div className="backupFileInfoRow">
            <div className="fileDirectory">{displayDirectoryString(fileDir)}</div>
            <div className="lastModified">{latestBackupDate}</div>
          </div>
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField>
          <div className="backupFileInfoRow">
            {recoverButton}
            {deleteButton}
          </div>
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

class BackupModal extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      backupData: null
    };
    this.onBackupDataReceived = this.onBackupDataReceived.bind(this);
    this.onRecoverClick = this.onRecoverClick.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
  }

  componentDidMount() {
    this._pubSubDataReceivedToken = PubSub.subscribe(PubSubTopics.backupDataReceived, this.onBackupDataReceived);
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.BACKUP_MODAL_OPENED
    });
    this.requestDataRefresh();
    this._pubSubEscapeKeyDownToken = PubSub.subscribe(PubSubTopics.escapeKeyDown, this.props.onCloseClick);
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubDataReceivedToken);
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.BACKUP_MODAL_CLOSED
    });
    PubSub.unsubscribe(this._pubSubEscapeKeyDownToken);
  }

  requestDataRefresh() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.REQUEST_BACKUP_DATA
    });
  }

  onBackupDataReceived(msg, data) {
    this.setState({
      backupData: data
    });
  }

  onRecoverClick(origPath, backupFile) {
    const data = {
      msg: nativeCommsBroadcastMessageTypes.RESTORE_BACKUP,
      origPath,
      backupFile
    };

    PubSub.publish(PubSubTopics.sendNativeMessage, data);
  }

  onDeleteClick(msg, filepath) {
    let { fileName } = splitFilePath(filepath);
    let actionMsg =
      msg === nativeCommsBroadcastMessageTypes.DELETE_ALL_BACKUPS_FOR_FILE ? "all backups" : "all but the latest";
    let alertMsg = `Are you sure you want to delete ${actionMsg} of ${fileName}?`;

    dialogs.okCancelPrompt(alertMsg, result => {
      if (result) {
        const data = {
          msg,
          filepath
        };
        PubSub.publish(PubSubTopics.sendNativeMessage, data);
        this.requestDataRefresh();
      }
    });
  }

  renderSettings() {
    return (
      <ModalGroup>
        <ModalRow>
          <ModalField flex={1}>
            <FormCheckbox
              label="Enable automatic backup (recommended)"
              checkboxState={this.props.autoBackupEnabled}
              onChange={this.props.onAutoBackupChange}
            />
          </ModalField>
        </ModalRow>
      </ModalGroup>
    );
  }

  renderPathOption() {
    return (
      <ModalGroup label="Backup Path">
        <ModalRow>
          <ModalField flex={5}>
            <FormTextField value={this.props.autoBackupLocation} disabled={true} />
          </ModalField>
          <ModalField flex={1}>
            <FormButton label="Change..." onClick={this.onBrowseClick} disabled={!this.props.autoBackupEnabled} />
          </ModalField>
        </ModalRow>
      </ModalGroup>
    );
  }

  renderLoading() {
    return (
      <ModalGroup label="Explore Backups">
        <ModalRow>
          <ModalField flex={1}>
            <PropagateLoader size={15} color={"#ffffff"} />
          </ModalField>
        </ModalRow>
      </ModalGroup>
    );
  }

  renderFileInfo() {
    const msg = !this.state.backupData
      ? "Loading current backup information..."
      : this.state.backupData.length === 0
        ? "There are no current backup files"
        : null;
    const orderedGroups = this.state.backupData ? reverse(sortBy(this.state.backupData, g => g.latestBackupDate)) : [];
    const groupComponents = map(orderedGroups, group =>
      renderFileGroup(group, this.onRecoverClick, this.onDeleteClick)
    );

    const child = orderedGroups.length ? groupComponents : <div>{msg}</div>;

    return (
      <ModalGroup label="Explore Backups">
        <div className="backupModalFilesList">{child}</div>
      </ModalGroup>
    );
  }

  render() {
    let modalHeading = null;
    let modalFooter = null;
    let fixedHeight = null;

    // #!if iOS === true
// modalHeading = (
// <ModalHeading
// title="Manage Automatic Backups"
// showDone={true}
// doneText={"Close"}
// onDoneClick={this.props.onCloseClick}
// />
// );
// fixedHeight = Dimensions.MODAL_MAX_HEIGHT;
    // #!else
    modalHeading = (
      <ModalHeading title="Manage Automatic Backups" showCancel={true} onCancelClick={this.props.onCloseClick} />
    );
    // #!endif

    return (
      <ModalView header={modalHeading} footer={modalFooter} fixedHeight={fixedHeight}>
        <ModalBody>
          <ModalDetailView>
            {this.renderSettings()}
            {this.renderFileInfo()}
            {/* {this.renderPathOption()} */}
          </ModalDetailView>
        </ModalBody>
      </ModalView>
    );
  }
}

BackupModal.propTypes = {
  autoBackupEnabled: PropTypes.bool.isRequired,
  autoBackupLocation: PropTypes.string,

  onCloseClick: PropTypes.func.isRequired,
  onAutoBackupChange: PropTypes.func.isRequired,
  onAutoBackupLocationChange: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  return {
    autoBackupEnabled: preferencesSelectors.getAutoBackupEnabled(state.present),
    autoBackupLocation: preferencesSelectors.getAutoBackupLocation(state.present)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onCloseClick: () => {
      dispatch(uiStateOperations.hideBackupModal());
    },
    onAutoBackupChange: value => {
      dispatch(preferencesOperations.setAutoBackup(value));
    },
    onAutoBackupLocationChange: value => {
      dispatch(preferencesOperations.setAutoBackupLocation(value));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BackupModal);
